/*
 * Which administration paths does this battery offer?
 *
 * There is no battery-level type field (wici-api models/battery.js carries only
 * name/developers/examiners/tests), so what a battery supports is derived from
 * its tests' `mode` — the one signal that distinguishes a rating scale a rater
 * fills out on their own from a performance-based test an examiner administers.
 *
 * PIN-210: the navigation uses this so an examiner cannot start an ANCR "rater
 * invite" or send a STARS form through the examiner-administered session flow.
 */

import axios from 'axios';

// wici-api's test.mode defaults to 'adaptive' when absent, so an unset mode is a
// performance-based test rather than an unknown one.
const modeOf = (test) => (test && test.mode) || 'adaptive';

// A rater completes this on their own, unauthenticated, from a shared link.
export const isInvitableTest = (test) => modeOf(test) === 'questionnaire';

// An examiner administers this one item at a time, signed in.
export const isSessionTest = (test) => modeOf(test) !== 'questionnaire';

/*
 * Capabilities rather than a single "kind". A battery that mixes modes gets both
 * options, each page filtering its own dropdown — deriving one kind meant a
 * single questionnaire test hid Create Test Session entirely and left that
 * battery's adaptive tests unreachable.
 *
 * A failed lookup is not an empty battery: offer both and let the server reject
 * an invalid choice, rather than stranding the examiner with no option, no
 * message and no retry on a transient error that looks exactly like a
 * correctly-configured battery.
 */
export const deriveBatteryCapabilities = (result) => {
  const tests = (result && result.tests) || [];
  if (result && result.failed) return { canInvite: true, canCreateSession: true };

  return {
    canInvite: tests.some(isInvitableTest),
    canCreateSession: tests.some(isSessionTest)
  };
};

/*
  The side menu and the page it renders both need the same battery's tests on the
  same view, so without this they fire two identical POSTs per page load. Keyed
  by battery and token, and cleared as soon as the request settles, so this
  collapses the concurrent pair without ever serving a stale answer on a later
  visit.
*/
const inFlight = new Map();

const requestBatteryTests = async (batteryID, token) => {
  const config = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Token ' + token
    }
  };

  try {
    const response = await axios.post(
      process.env.REACT_APP_API_URL + '/battery/testDirectory',
      { batteryID },
      config
    );

    const tests = response && response.data && response.data.tests;
    // A response without a tests array is a malformed answer, not an empty
    // battery — the caller must be able to tell those apart.
    if (!Array.isArray(tests)) return { tests: [], failed: true };
    return { tests, failed: false };
  } catch (error) {
    console.log(error);
    return { tests: [], failed: true };
  }
};

export const fetchBatteryTests = (batteryID, token) => {
  const key = `${batteryID}:${token}`;
  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = requestBatteryTests(batteryID, token).finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
};
